import type { Messages } from "./en";

/**
 * Simplified Chinese catalogue.
 *
 * Typed as `Messages`, so the compiler rejects a missing or misspelled key.
 * That is what stops an English string appearing in the middle of a Chinese
 * page.
 *
 * Marketing copy is written to read naturally in Chinese rather than
 * mirroring the English sentence by sentence; the meaning is kept, the
 * phrasing is not. The non-clinical disclaimers are the exception and are
 * translated closely, because a loose rendering there would change what is
 * being promised. The US crisis number (988) is kept and labelled as United
 * States only, with the instruction to use local services elsewhere.
 *
 * Note that choosing Chinese changes language only. It does not move the
 * user to a different backend, region or data store.
 */
export const zhCN: Messages = {
  meta: {
    siteName: "GrowthWise Education",
    tagline: "面向人生的教育",
    home: {
      title: "GrowthWise Education | 人生充满挑战，学会从容应对",
      description:
        "面向人生的教育。GrowthWise 帮助人们理解挫折、压力与负面情绪如何影响信心和表现，培养持续成长所需的韧性。",
    },
    about: {
      title: "关于我们 | GrowthWise Education",
      description:
        "我们的使命与理念：教育不仅要让人为成功做好准备，也要让人在生活不如预期时，懂得如何建设性地应对。",
    },
    services: {
      title: "课程与服务 | GrowthWise Education",
      description:
        "学生项目、家长教育、职场与高管项目、团体培训、一对一咨询。支持线下与线上开展。",
    },
    assessment: {
      title: "GrowthWise 自查 | GrowthWise Education",
      description:
        "约 90 秒的私密自我反思，帮你看清一段困难经历正在如何影响自己。作答只保留在你的浏览器中，不会被保存。",
    },
    partnerships: {
      title: "合作 | GrowthWise Education",
      description:
        "与高校、中小学及企业机构建立长期合作，共同培养韧性、促进情绪健康，帮助人们为真实生活做好准备。",
    },
    careers: {
      title: "研究与测评岗位 | GrowthWise Education",
      description:
        "加入 GrowthWise，研究困难经历、情绪与韧性如何影响一个人的自我认知与表现。",
    },
    contact: {
      title: "联系我们 | GrowthWise Education",
      description:
        "告诉我们你或你的机构正在面对什么，我们会帮你找到最合适的起点。",
    },
    videoIntro: {
      title: "视频自我介绍 | GrowthWise Education",
      description: "作为申请的一部分，在浏览器中录制一段简短的视频自我介绍。",
    },
    privacy: {
      title: "隐私说明 | GrowthWise Education",
      description: "GrowthWise Education 收集哪些信息，以及为什么收集。",
    },
    terms: {
      title: "使用条款 | GrowthWise Education",
      description: "适用于 GrowthWise Education 网站与用户中心的条款。",
    },
    notFound: {
      title: "页面未找到 | GrowthWise Education",
      description: "你请求的页面不存在。",
    },
  },

  nav: {
    skipToContent: "跳到主要内容",
    primary: "主导航",
    home: "首页",
    about: "关于我们",
    services: "课程与服务",
    assessment: "自查",
    partnerships: "合作",
    careers: "加入我们",
    contact: "联系我们",
    signIn: "登录",
    signOut: "退出登录",
    account: "个人中心",
    admin: "管理后台",
    openMenu: "打开菜单",
    closeMenu: "关闭菜单",
    language: "语言",
    languageEnglish: "English",
    languageChinese: "简体中文",
  },

  footer: {
    explore: "浏览",
    account: "账户",
    legal: "法律条款",
    privacy: "隐私说明",
    terms: "使用条款",
    rights: "保留所有权利。",
    crisisHeading: "紧急支持",
    crisisBody:
      "在美国境内如需紧急或危机支持，请拨打或发送短信至 988。在其他国家或地区，请联系当地相应的紧急或危机支持服务。",
    notMedical:
      "GrowthWise Education 提供的是教育项目，不能替代具备资质的医疗或心理健康专业人员的评估与照护。",
  },

  home: {
    heroTitleLine1: "人生充满挑战，",
    heroTitleLine2: "学会从容应对。",
    heroBody:
      "学校为考试和职业做准备，但人生的考题远不止于此。挫折、变化与不确定，是每段人生都绕不开的功课；如何面对它们，决定了我们会成为怎样的人。GrowthWise 帮助人们读懂这些经历，带着韧性、信心与清醒的判断，从中成长。",
    pillarsHeading: "我们帮助你做到",
    pillars: {
      understand: {
        title: "理解经历",
        body: "看清发生了什么，以及它为什么会这样影响你。",
      },
      protect: {
        title: "守护信心",
        body: "不让一次挫折，变成对自己的全盘否定。",
      },
      build: {
        title: "培养韧性",
        body: "增强复原、适应和做出明智判断的能力。",
      },
      grow: {
        title: "持续成长",
        body: "带着更开阔的视角，重新投入真正重要的目标。",
      },
    },
    missionEyebrow: "我们的使命",
    missionTitle: "既为成功做准备，也为人生的不如意做准备",
    missionBody:
      "学校、家庭和职场在“如何成功”上投入了大量心血，却很少有人教我们，当失败、拒绝、压力、失去与突如其来的变故降临时，该如何面对。而恰恰是这些时刻，长久地影响着一个人的信心、判断与表现。我们的使命，是帮助人们读懂这些经历，并在其中成长，让挫折成为人生故事的一部分，而不是故事的结局。",
    missionLink: "进一步了解我们",
    missionImageAlt: "插画：登山途中，一个人回身伸手，拉同伴登上陡峭的岩坡。",
    focusEyebrow: "我们的重心",
    focusTitle: "耐挫教育",
    focusBody:
      "每个人都被教导要追求成功，却很少有人学过失败之后该怎么办。研究表明，人并不会自动从失败中吸取教训：失败需要经过有引导的反思与意义建构，才能转化为洞见，成为日后成功的养分。耐挫教育提供的正是这样的引导：帮助人们认识失败在情绪、认知和行为上带来的影响，赶在挫折变成自我标签之前及时化解，把经历转化为成长。我们用四个阶段的学习框架，把这一过程落到实处。",
    frameworkHeading: "我们的学习框架",
    modelLink: "了解学习框架",
    servicesEyebrow: "课程与服务",
    servicesSubtitle: "面向学生、家庭、职场人士与各类机构",
    servicesLink: "查看全部课程",
    servicesStartTitle: "不确定从哪里开始？",
    servicesStartBody:
      "告诉我们你或你的机构正在面对什么，我们会帮你找到最合适的起点。",
    servicesStartLink: "和我们聊聊",
    assessmentEyebrow: "自我反思工具",
    assessmentLink: "开始自查",
    partnersEyebrow: "与我们合作",
    partnersSubtitle:
      "我们与中小学、高校及各类机构携手合作，同时也在壮大自己的研究团队。",
    partners: {
      universities: {
        title: "高校",
        body: "联合研究、共建课程与证书项目、项目成效评估。",
      },
      schools: {
        title: "中小学",
        body: "学生工作坊、韧性课程、教师培训，以及“韧性学校计划”。",
      },
      organizations: {
        title: "企业与机构",
        body: "赞助合作、员工与管理者培训、领导者韧性专题项目。",
      },
      researchers: {
        title: "研究人才",
        body: "和我们一起研究困难经历、情绪与韧性如何影响人的表现。",
      },
    },
    partnersLink: "了解合作方式",
    careersLink: "查看研究岗位",
    ctaTitle: "接下来，可以不一样",
    ctaBody: "告诉我们你或你的机构正在面对什么，我们会帮你找到最合适的起点。",
    ctaButton: "联系我们",
    ctaSecondary: "创建账户",
  },

  approach: {
    title: "GrowthWise 的不同之处",
    body: "GrowthWise 处在传统教育、励志培训与临床心理服务之间的独特位置。我们不只是教人如何成功，不靠鼓励人“再努力一点”，也不治疗心理疾病。我们关注的，是经历与表现之间发生了什么。",
    chainIntro: "我们的工作，围绕这样一条关系链展开：",
    chainLabel: "一段困难经历，如何一步步影响表现",
    chain: ["经历", "情绪", "自我认知", "动机与判断", "行为", "表现"],
    note: "失败与挫折是我们的重点方向，因为它们让这一过程格外清晰；但我们的使命不止于此。GrowthWise 帮助人们理解困难经历如何塑造自己的思考、感受、决策与表现，并培养在人生各个阶段建设性应对所需的韧性、信心、判断力与适应力。",
  },

  about: {
    eyebrow: "关于我们",
    title: "关于 GrowthWise Education",
    subtitle:
      "我们把这称为面向人生的教育：不只学习如何成功，也学习当生活不如预期时如何应对。",
    whoHeading: "我们是谁",
    whoBody:
      "GrowthWise Education 是一家多学科融合的教育与个人发展公司，致力于帮助人们更好地认识自己，培养在学习、工作、人际关系与人生中从容前行所需的内在能力。我们融汇教育学、心理学、行为科学、法学与真实世界的实践经验，汇聚一流的教育者、心理学家、研究人员与法学学者，共同关注教育中一个至关重要却常被忽视的维度：当人生变得艰难时，人们如何应对。",
    missionHeading: "我们的使命",
    missionBody:
      "我们的使命，是帮助人们理解挫折、压力、不确定、失望与变化如何影响自己的思考、感受、决策与表现，并培养建设性地继续前行所需的韧性、信心、判断力与自我觉察。",
    missionDetail:
      "我们相信，教育不仅要让人能够取得成就、获得成功，也要让人在生活不如预期时，依然能够复原、适应、守住健康的自我价值感，在压力之下做出明智的选择，并持续成长。",
    coreIdeaLabel: "核心理念",
    coreIdeaBody:
      "我们不会抹去困难的经历，而是帮助人们从中学习，同时不被它定义。",
    coreIdeaDiagram:
      "一条曲线在困难经历处下探，随后随着人从中学习、成长而重新上扬；另一条渐隐的虚线，代表被经历定义的那条路。",
    coreIdeaSetback: "困难经历",
    coreIdeaGrow: "从中学习，继续成长",
    coreIdeaDefined: "被它定义",
    prepareHeading: "既为成功做准备，也为生活不如预期时做准备",
    prepareBody: [
      "GrowthWise Education 把教育的意义延伸到知识、技能与成就之外。学校、大学、家庭与职场为帮助人们取得成功投入了大量资源，却很少有人帮助他们理解和应对失败、失望、拒绝、压力、不确定、失去、冲突与重大变化所带来的情绪冲击。",
      "这些经历之所以重要，是因为一个人的表现不只取决于能力，也受到情绪、自我认知、动机、判断与行为的影响。困难的经历可能削弱信心、扭曲自我价值感、消磨动力、干扰决策，并影响人们面对未来机会的方式。",
      "GrowthWise 关注的，正是教育中这个常被忽视的维度：帮助人们理解经历、情绪、自我认知、行为与表现之间的关联，同时培养建设性应对、持续成长所需的韧性、自我觉察、信心与适应力。",
    ],
    usableBody:
      "一个人可能知识扎实、能力出众，却依然会在信心崩塌、焦虑难以招架、被拒绝改变了对自己的看法，或失败让人不敢再尝试时，难以发挥应有的水平。",
    usableClosing:
      "GrowthWise 培养的，正是让知识与能力在真实生活中依然“用得上”的内在力量。",
    lifeHeading: "面向人生的教育，包括培养：",
    lifeItems: [
      "自我觉察",
      "情绪理解",
      "自信",
      "健康的自我价值感",
      "韧性",
      "内在动力",
      "判断力",
      "适应力",
      "自主感",
      "人际关系",
      "从经历中学习的能力",
    ],
    beingNote:
      "这种更宽广的个人成长教育，在哲学上有时被称为“存在教育”（Being Education）。它并不取代传统教育，而是与之相辅相成。",
    visionHeading: "我们的愿景",
    visionBody:
      "我们期待这样一个未来：教育不仅让人有能力取得成就，也让人能以更强的韧性和更开阔的视角，面对逆境、不确定、变化与失望。",
    visionDetail:
      "在那样的未来里，学生不会用失败来定义自己；家长和教育者懂得如何建设性地回应挫折；学校与职场把困难经历视为学习的契机，而不是恐惧或污名的来源。",
    visionGoal:
      "我们的长期愿景，是让情绪韧性、健康的自我价值感和从挫折中学习的能力，成为贯穿一生的教育中不可或缺的一部分。",
  },

  app: {
    heading: "GrowthWise Education 应用",
    body: "应用通过简短课程、私密反思、教育性自查与实用练习，把学习延伸到日常生活中。它是对人际指导、医疗照护或专业心理健康服务的补充，而不是替代。",
    point1: "私密反思：个人笔记与情绪记录只有你自己能看到。",
    point2: "小而实际的步骤：为真实的日常设计的简短练习。",
    point3: "连贯的学习：在工作坊、咨询与团体课程之间持续推进。",
  },

  services: {
    eyebrow: "课程与服务",
    title: "覆盖人生各个阶段的实用教育",
    subtitle:
      "我们的课程聚焦于困难经历与负面情绪如何影响信心、自我价值感、动力、判断与表现，以及人们如何以更强的韧性、更开阔的视角去应对。",
    items: {
      students: {
        index: "01",
        title: "学生项目",
        summary:
          "帮助初中至研究生阶段的学生应对学业挫折、成绩压力、完美主义与人生转折。",
        body: "面向初中、高中、本科及研究生阶段的学生，帮助他们理解并应对学业挫折、成绩压力、完美主义、对失败的恐惧、被拒绝、信心受挫，以及升学、留学等重大转折。",
        detail:
          "目标是帮助学生增强韧性、守护自我价值感、重燃学习动力，并在经历挫折之后，有信心重新出发。",
        points: [
          "工作坊",
          "系列课程",
          "夏季项目",
          "领导力培训",
          "线上课程与数字化模块",
        ],
      },
      parents: {
        index: "02",
        title: "家长教育",
        summary:
          "帮助家长看清期望、比较以及面对挫折时的反应，会如何影响孩子的信心与自我价值感。",
        body: "孩子如何看待成功、失望与失败，很大程度上受父母影响。我们帮助家长了解：期望、比较、表扬、批评，以及自己面对孩子受挫时的反应，会如何影响孩子的信心、动力与自我价值感。",
        detail:
          "话题包括：学业压力、如何陪伴成绩优异的孩子、对失败的恐惧、孩子受挫后如何沟通，以及如何在不轻视孩子情绪的前提下培养韧性。",
        points: ["家长工作坊", "线上讲座", "家庭课程", "教育咨询"],
      },
      professionals: {
        index: "03",
        title: "职场与高管项目",
        summary:
          "帮助职场人士与管理者应对职业挫折、项目失利、批评、组织变革与领导压力。",
        body: "帮助职场人士与管理者更有效地应对职业挫折、项目失利、批评、组织变革、不确定性，以及领导岗位上的压力。",
        detail:
          "课程聚焦职业韧性、情绪压力下的决策、挫折后的信心重建、适应性领导力、职业身份认同以及团队复原。",
        points: ["个人", "管理团队", "企业", "行业协会", "非营利机构"],
      },
      group: {
        index: "04",
        title: "团体培训",
        summary:
          "由引导师带领，结合系统讲授、引导式反思、案例研讨与同伴学习的团体课程。",
        body: "由专业引导师带领的团体课程，结合系统讲授、引导式反思、案例研讨、实践练习、小组讨论与同伴学习。",
        detail:
          "看看别人如何面对相似的压力与挫折，往往能带来意想不到的启发。对很多人来说，团体培训正是这样的契机。",
        points: [
          "多周系列课程",
          "同期班",
          "学生小组",
          "家长小组",
          "管理者小组",
        ],
      },
      individual: {
        index: "05",
        title: "一对一咨询",
        summary: "围绕困难经历、情绪反应、信心与下一步方向的一对一教育咨询。",
        body: "一对一的教育咨询，围绕困难经历、情绪反应、信心、表现模式以及前行策略展开。",
        detail:
          "这项服务属于教育范畴，而非临床服务。GrowthWise 不诊断、也不治疗任何心理健康问题；如涉及临床层面的困扰，我们会建议当事人向具备资质的医疗或心理健康专业人员寻求帮助。",
        points: ["一对一面谈", "单次或多次", "线下或线上"],
      },
    },
    deliveryTitle: "服务方式",
    deliveryOnSiteLabel: "线下",
    deliveryOnSiteBody: "工作坊、讲座与一对一指导",
    deliveryRemoteLabel: "线上",
    deliveryRemoteBody: "不受地域限制的课程与咨询",
    deliveryAppLabel: "数字化",
    deliveryAppBody: "线上学习、反思与自查工具",
    pricingNoteHeading: "费用与排期",
    pricingNoteBody:
      "项目会根据不同对象确定内容与范围，因此费用与排期需要直接沟通确认。请告诉我们你正在面对的情况，我们会为你介绍可选方案。",
    cta: "沟通课程方案",
  },

  model: {
    title: "GrowthWise 学习框架",
    intro:
      "GrowthWise 的课程遵循四个阶段的学习过程，旨在帮助人们理解困难经历、减轻其负面影响，并带着更强的韧性与信心继续前行。",
    steps: {
      recognize: {
        number: "1",
        title: "识别",
        summary:
          "觉察困难经历之后的情绪、想法与反应，并把它们与“我是谁”区分开。",
        body: "觉察困难经历之后出现的情绪、思维模式、动力变化与行为反应。觉察越清晰，就越能把经历本身与一个人的身份和自我价值区分开来。",
      },
      reframe: {
        number: "2",
        title: "重构",
        summary: "更准确地看待发生的事：一次挫折，不是对你价值或未来的判决。",
        body: "对发生的事形成更平衡、更准确的理解。学员将学会区分：挫折与个人价值，暂时的困难与永久的无能，情绪反应与客观事实。",
      },
      rebuild: {
        number: "3",
        title: "重建",
        summary: "重建信心、动力、情绪平衡，以及对生活的掌控感。",
        body: "增强复原与前行所需的内在资源，包括信心、自我价值感、动力、情绪调节能力、理性判断，以及重新找回的掌控感。",
      },
      reengage: {
        number: "4",
        title: "再出发",
        summary: "把理解化为行动，重新追求有意义的目标。",
        body: "把理解转化为行动：重新面对挑战，以更开阔的视角做决定，建设性地运用反馈，重建良好的日常节奏，追求有意义的目标，不让过去的挫折限定未来的可能。",
      },
    },
  },

  assessment: {
    eyebrow: "自我反思工具",
    title: "GrowthWise 自查",
    subtitle: "约 90 秒的私密反思",
    intro:
      "这是一个简短的教育性自我反思工具，帮助你思考一段困难经历可能正在如何影响你的情绪、信心、动力、自我价值感、行为、人际关系与表现。它的目的是增进自我觉察，并帮你找到合适的学习资源。作答全程在你的浏览器中完成，不会被传输，也不会被保存。",
    disclaimerHeading: "重要说明",
    disclaimerBody:
      "GrowthWise 自查不是经过验证的临床量表，也不用于诊断任何心理健康问题。它不能替代具备资质的医疗或心理健康专业人员的评估与照护。",
    privacyNotice:
      "你在本页面上的任何选择都不会离开你的设备。没有任何作答会发送到我们的服务器，关闭页面即被丢弃。",
    questionsHeading: "反思问题",
    questions: [
      "在过去两周里，关于某段困难经历的念头，有多常让你难以放下？",
      "这段经历在多大程度上影响了你做决定时的信心？",
      "你有多常回避与这段经历有关的人、场合或事情？",
      "这段经历在多大程度上影响了你的睡眠、注意力或日常精力？",
      "想象一个积极的下一步，对你来说有多难？",
      "你有多常觉得，自己被那件事定义了，而不是被完整的自己定义？",
    ],
    optionsLabel: "选项",
    options: ["完全没有", "有几天", "超过一半的日子", "几乎每天"],
    progressLabel: "已回答 {answered} / {total}",
    submit: "查看我的反思结果",
    reset: "重新开始",
    incomplete: "请回答全部问题后再查看结果。",
    resultEyebrow: "你的反思结果",
    resultTitle: "你的作答反映了什么",
    resultSubtitle: "仅提供相应的建议，不构成临床诊断",
    nextStepLabel: "可考虑的下一步",
    bands: {
      lower: {
        label: "当前影响较低",
        headline: "你有不错的空间去反思，并进一步增强韧性。",
        body: "这段经历对日常生活的影响看起来相对有限。教育项目与预防性的韧性练习，可以帮助你在这份稳定的基础上继续成长。",
        nextStep:
          "可以考虑参加一场工作坊、一门短期课程，或使用自助式的数字化学习模块。",
      },
      moderate: {
        label: "当前影响中等",
        headline: "一个有结构的下一步，可能会有帮助。",
        body: "这段经历可能仍在影响你的信心、注意力或投入程度。有针对性的指导，或许能帮你看清其中的模式，并找到一个力所能及的下一步。",
        nextStep: "可以考虑一对一咨询，或由引导师带领的团体课程。",
      },
      higher: {
        label: "当前影响较高",
        headline: "你值得在这件事上得到支持，而不是更多压力。",
        body: "这些回答显示出相当程度的当前困扰。教育性项目可以作为补充，但同时建议获得适当的个人支持或专业支持。",
        nextStep: "建议联系具备资质的心理健康专业人员或医疗服务提供者。",
      },
    },
  },

  partnerships: {
    eyebrow: "合作",
    title: "携手共建韧性文化",
    subtitle:
      "GrowthWise 与教育机构及各类组织建立长期合作，共同在社群中增强韧性、促进情绪健康，帮助人们为真实生活做好准备。",
    intro:
      "我们不只是一家外部的工作坊供应方，而是与合作伙伴一起设计项目、开发课程、评估成效，建立可持续的教育能力。",
    universities: {
      title: "高校与高等教育机构",
      body: "我们与教育学院、心理学系、学生事务部门、心理健康项目、国际学生服务中心、领导力中心、高管教育项目及研究机构开展合作。",
      detail:
        "合作方向涵盖耐挫教育、韧性、信心、情绪健康、学生发展与表现等主题。",
      points: [
        "联合研究",
        "共建课程与证书项目",
        "试点项目",
        "教师合作",
        "项目评估",
        "学术会议与论坛",
      ],
    },
    schools: {
      title: "中小学（K–12）",
      body: "我们与公立、私立、特许及国际学校合作，把韧性教育与耐挫教育融入学生发展之中。",
      points: [
        "学生工作坊",
        "韧性课程",
        "家长教育",
        "教师培训",
        "高成就学生支持",
        "升学衔接项目",
        "全校情绪健康计划",
      ],
    },
    resilientSchool: {
      badge: "旗舰计划",
      title: "GrowthWise 韧性学校计划",
      body: "面向希望系统推进的学校，“韧性学校计划”可以整合学生课程、教师发展、家长教育、数字资源、测评、跟踪支持与机构报告。",
      detail:
        "我们希望与学校建立长期合作，共同培育更深厚的韧性文化：让学生更懂得理解挫折、调适负面情绪、守护信心与自我价值感，并持续学习、不断成长。",
      points: [
        "学生课程",
        "教师发展",
        "家长教育",
        "数字资源",
        "测评",
        "跟踪支持",
        "机构报告",
      ],
    },
    corporate: {
      title: "企业赞助与合作",
      body: "我们与企业、专业服务公司、基金会、创业项目、加速器、领导力学院及行业协会合作，与所有同样关注韧性、情绪健康、教育与人的发展的伙伴同行。",
      formsHeading: "合作形式",
      forms: [
        "赞助学生与社区项目",
        "支持研究与测评",
        "共建教育项目",
        "员工与管理者培训",
        "公共论坛",
        "奖学金",
        "数字学习资源",
        "联合公益倡导",
      ],
      topicsHeading: "面向企业的主题",
      topics: [
        "领导者韧性",
        "压力下的决策",
        "项目失利后的团队复原",
        "组织变革",
        "职业自信",
        "心理安全感",
        "从“明智的失败”中学习",
      ],
      closing:
        "我们希望建立的合作，既能创造社会影响，也能为组织带来价值，帮助个人与社群更好地应对压力、挫折、不确定与变化。",
    },
    ctaTitle: "探讨合作可能",
    ctaBody:
      "告诉我们你所在的机构，以及你希望实现的目标。我们会与你联系，一起探讨最合适的合作方式。",
    ctaButton: "开始沟通",
  },

  careers: {
    eyebrow: "加入我们",
    title: "研究与测评岗位",
    subtitle:
      "GrowthWise 正在招募研究与测评方面的专业人才。如果你关注困难经历、情绪、韧性、自我认知与表现之间的关系，欢迎加入我们。",
    agendaHeading: "我们的研究方向",
    agenda: [
      "学生如何解读失败",
      "挫折与信心的关系",
      "面对学业困难时的情绪反应",
      "成绩压力",
      "完美主义",
      "韧性",
      "家长如何回应孩子的挫折",
      "国际学生的适应",
      "成就观与失败观的文化差异",
    ],
    contributeHeading: "你可能参与的工作",
    contribute: [
      "文献综述与研究设计",
      "开发教育调查问卷与测评工具",
      "收集并分析定量与定性数据",
      "评估 GrowthWise 项目与学习成效",
      "撰写研究报告、文章与教育材料",
      "开发循证干预方案",
      "与高校、中小学及研究伙伴协作",
    ],
    backgroundHeading: "我们希望你具备",
    backgroundBody:
      "心理学、教育学、人类发展、行为科学、心理咨询、测评、统计学或相关领域背景。具备研究方法、项目评估、心理测量或跨文化研究经验者尤佳。",
    goalHeading: "这项工作的意义",
    goalBody:
      "GrowthWise 致力于为教育项目打下坚实的研究基础，并逐步开发经过验证的测评工具与循证方法，帮助个人与机构更好地理解并应对失败、挫折、负面情绪与韧性议题。",
    arrangementsHeading: "合作方式",
    arrangementsBody:
      "我们欢迎研究人员、研究生、博士后、高校教师以及资深从业者与我们联系。",
    arrangements: ["全职", "兼职", "项目制", "研究合作"],
    ctaTitle: "有意加入？",
    ctaBody:
      "请简要介绍你的背景与研究兴趣。你也可以创建账户，在线提交申请并录制一段视频自我介绍。",
    ctaButton: "发送咨询",
    ctaSecondary: "创建账户并申请",
  },

  contact: {
    eyebrow: "联系我们",
    title: "接下来，可以不一样",
    subtitle: "告诉我们你或你的机构正在面对什么，我们会帮你找到最合适的起点。",
    siteLabel: "访问 GrowthWise Education 网站",
    formHeading: "发送消息",
    formIntro: "发送这条消息不需要注册账户。",
    successHeading: "消息已收到",
    successBody: "谢谢。我们已收到你的消息，会回复到你填写的邮箱地址。",
    sendAnother: "再发送一条消息",
    checkInPrompt: "想要使用自查工具？",
  },
  form: {
    name: "姓名",
    email: "电子邮箱",
    organization: "所属机构",
    organizationOptional: "所属机构（选填）",
    phone: "联系电话（选填）",
    message: "你正在面对什么？",
    messageHint: "几句话即可。请不要填写敏感的健康信息。",
    interest: "你对什么感兴趣？",
    audience: "这是为谁咨询的？",
    delivery: "偏好的交付方式",
    preferredLanguage: "回复时使用的语言",
    required: "必填",
    optional: "选填",
    selectPlaceholder: "请选择",
    charactersRemaining: "还可输入 {count} 个字符",
    consentContact:
      "我同意 GrowthWise Education 就本次咨询通过上述邮箱与我联系。",
    consentPrivacy: "我已阅读隐私说明，并同意其中关于信息处理方式的内容。",
    submit: "发送消息",
    submitting: "正在发送…",
    saveDraft: "保存草稿",
    saving: "正在保存…",
    saved: "草稿已保存",
    submitApplication: "提交申请",
    interests: {
      lectures_and_seminars: "讲座与研讨",
      individual_consulting: "一对一咨询",
      group_training: "团体培训",
      digital_learning_and_support: "数字化学习与支持",
      not_sure: "还不确定",
    },
    audiences: {
      individual: "个人",
      educational_community: "教育社群",
      organization: "机构或企业",
      community: "社区群体",
    },
    deliveries: {
      on_site: "现场",
      remote: "远程",
      either: "都可以",
    },
  },

  auth: {
    signInTitle: "登录",
    signInSubtitle: "查看你的申请与录制内容。",
    signUpTitle: "创建账户",
    signUpSubtitle: "注册后即可保存申请草稿、提交申请，并录制视频自我介绍。",
    password: "密码",
    passwordConfirm: "确认密码",
    passwordHint: "至少 12 个字符。",
    displayName: "你的称呼",
    signInButton: "登录",
    signUpButton: "创建账户",
    signOutButton: "退出登录",
    forgotPassword: "忘记密码？",
    noAccount: "还没有账户？",
    hasAccount: "已经有账户了？",
    createOne: "立即创建",
    signInInstead: "去登录",
    resetTitle: "重置密码",
    resetSubtitle: "填写你的邮箱地址，我们会发送一个链接用于设置新密码。",
    resetButton: "发送重置链接",
    resetSentHeading: "请查收邮件",
    resetSentBody:
      "如果 {email} 对应着一个账户，重置链接已在发送途中。链接会在短时间后失效。",
    verifyTitle: "验证你的邮箱地址",
    verifyBody:
      "我们已向 {email} 发送了验证链接。请打开链接完成验证，然后回到这里。",
    verifyResend: "重新发送验证邮件",
    verifyResent: "验证邮件已发送。",
    verifyRefresh: "我已完成验证，继续",
    verifyStillPending:
      "该邮箱仍未完成验证。请先打开邮件中的链接，然后再试一次。",
    verifiedBanner: "邮箱已验证，谢谢。",
    verificationRequired: "提交申请与录制视频都需要先完成邮箱验证。",
    sessionExpiredTitle: "登录状态已过期",
    sessionExpiredBody: "出于安全考虑，你已被退出登录。请重新登录。",
    passwordRequirements: "请使用至少 12 个字符。",
    passwordMismatch: "两次输入的密码不一致。",
    termsAgreement: "创建账户即表示你同意使用条款与隐私说明。",
  },

  account: {
    navOverview: "概览",
    navApplication: "我的申请",
    navVideos: "我的录制",
    navProfile: "个人资料",
    overviewTitle: "欢迎回来，{name}",
    overviewSubtitle: "在这里管理你的申请与录制内容。",
    statusHeading: "账户状态",
    emailVerified: "邮箱已验证",
    emailUnverified: "邮箱未验证",
    applicationsHeading: "申请",
    videosHeading: "录制",
    newApplication: "开始填写申请",
    continueDraft: "继续填写草稿",
    viewApplication: "查看",
    noApplications: "你还没有开始填写申请。",
    noVideos: "你还没有录制视频自我介绍。",
    recordVideo: "录制视频自我介绍",
    profileTitle: "个人资料",
    profileSubtitle: "更新我们联系你时使用的信息。",
    profileEmailNote: "登录邮箱无法在此处修改。如需变更，请与我们联系。",
    profileSaved: "资料已更新。",
    applicationTitle: "申请",
    applicationSubtitle: "你可以随时保存草稿，所有必填项完成后即可提交。",
    applicationSubmittedHeading: "申请已提交",
    applicationSubmittedBody:
      "我们已收到你的申请。你仍可以在此查看，我们会就后续步骤与你联系。",
    draftBadge: "草稿",
    deleteDraft: "删除草稿",
    deleteDraftConfirmTitle: "确定删除这份草稿吗？",
    deleteDraftConfirmBody:
      "草稿以及关联的录制内容都会被删除，此操作无法撤销。",
    deleteVideoConfirmTitle: "确定删除这段录制吗？",
    deleteVideoConfirmBody: "视频文件将被永久删除，此操作无法撤销。",
    confirmDelete: "确认删除",
    cancel: "取消",
    deleted: "已删除。",
    deletePartial: "记录已删除，但有一个文件暂时未能删除，系统已加入重试队列。",
    statusLabel: "状态",
    createdLabel: "创建时间",
    updatedLabel: "最近更新",
    submittedLabel: "提交时间",
    attachedVideos: "已关联 {count} 段录制",
    videosTitle: "我的录制",
    videosSubtitle:
      "录制内容为私有。只有你本人和 GrowthWise Education 的工作人员可以播放。",
    videoFormat: "格式",
    videoSize: "文件大小",
    videoSource: "录制方式",
    videoSources: {
      browser_recording: "本网站录制",
      file_upload: "上传的文件",
    },
    playVideo: "播放",
    hideVideo: "收起",
    deleteVideo: "删除录制",
    videoDeleted: "录制已删除。",
    videoDeleteQueued: "该录制已从你的账户中移除，存储中的文件已加入删除队列。",
    playbackExpired: "此播放链接已过期。重新打开该录制即可获取新链接。",
  },

  contactStatus: {
    new: "新消息",
    in_progress: "处理中",
    answered: "已回复",
    spam: "垃圾信息",
    archived: "已归档",
  },

  submissionStatus: {
    draft: "草稿",
    submitted: "已提交",
    in_review: "审阅中",
    accepted: "已接受",
    declined: "未通过",
    withdrawn: "已撤回",
    archived: "已归档",
  },

  videoStatus: {
    pending_upload: "上传未完成",
    uploaded: "可播放",
    upload_failed: "上传失败",
    delete_pending: "待删除",
    deleted: "已删除",
  },

  record: {
    title: "视频自我介绍",
    intro:
      "直接在浏览器中录制一段简短的自我介绍。上传之前，你可以回看并重新录制。",
    permissionHeading: "开始之前",
    permissionBody:
      '相比纯文字表单，一段口头介绍能让我们更了解你的情况。点击"开启摄像头"后，浏览器会请求使用摄像头和麦克风的权限。在你主动点击开始之前，不会录制或发送任何内容。',
    limitsDuration: "最长时长：{duration}",
    limitsSize: "最大文件大小：{size}",
    limitsPrivacy:
      "录制内容为私有。没有公开链接，播放需要经过授权且有效期很短的地址。",
    enableCamera: "开启摄像头",
    requestingPermission: "正在等待摄像头权限…",
    cameraOff: "摄像头已关闭。",
    liveLabel: "摄像头实时预览",
    previewLabel: "录制内容回放",
    start: "开始录制",
    stop: "停止录制",
    processing: "正在生成录制文件…",
    switchCamera: "切换摄像头",
    turnOffCamera: "关闭摄像头",
    recordingStatus: "录制中 {elapsed} / {limit}",
    retake: "重新录制",
    clipSummary: "录制完成：{duration}，{size}，{type}",
    durationUnknown: "时长未知",
    limitDuration: "已达到最长时长，录制已自动停止。",
    limitSize: "已达到最大文件大小，录制已自动停止。",
    upload: "上传这段录制",
    retryUpload: "重新上传",
    authorizing: "正在获取上传授权…",
    uploadingLabel: "上传中 {sent} / {total}",
    cancelUpload: "取消上传",
    finalizing: "正在校验已上传的文件…",
    uploadComplete: "上传完成",
    uploadCompleteBody: "录制内容已保存并关联到你的账户。",
    uploadFailedHeading: "上传未完成",
    uploadFailedBody: "录制内容仍然保留，你可以直接重试，无需重新录制。",
    errorHeading: "录制出现问题",
    errors: {
      insecureContext: "使用摄像头需要安全连接",
      insecureContextBody:
        "浏览器仅在 HTTPS 下允许使用摄像头。请通过 HTTPS 打开本页面后重试。",
      unsupported: "此浏览器无法在页面内录制视频。请改用下方的文件上传方式。",
      permissionDenied:
        '摄像头或麦克风权限被拒绝。请在浏览器设置中允许访问，然后重新点击"开启摄像头"。',
      noDevice: "未检测到摄像头。请连接摄像头，或改用下方的文件上传方式。",
      deviceBusy: "摄像头正被其他应用占用。请关闭该应用后重试。",
      recordingFailed:
        "录制意外中断。此前已采集的画面保留在下方，你仍可以回看或上传。",
      interrupted:
        "录制被中断，可能是页面失去焦点或有来电。此前已采集的画面已保留。",
      emptyRecording: "没有采集到视频数据，请重新录制。",
      generic: "无法启动摄像头，请重试。",
    },
    fallbackHeading: "改为上传文件",
    fallbackBody:
      "如果你更习惯用手机或电脑的相机应用录制，也可以在这里上传文件。",
    fallbackRequired:
      "此浏览器无法在页面内录制视频，请用其他应用录制后在这里上传文件。",
    chooseFile: "选择视频文件",
    preferFile: "我想改为上传文件",
    fileWrongType: "该文件不是受支持的视频格式。",
    fileTooLarge: "该文件超过了 {limit} 的限制。",
  },

  admin: {
    title: "管理后台",
    subtitle: "申请、留言、账户与操作审计。",
    confidentialityNote:
      "此后台会显示他人的个人信息与录制内容，相关访问会被记录。",
    navSubmissions: "申请",
    navMessages: "留言",
    navUsers: "用户",
    navAudit: "审计日志",

    submissionsTitle: "申请",
    submissionsSubtitle: "筛选、查看并更新已提交的申请。",
    messagesTitle: "联系留言",
    messagesSubtitle: "来自公开联系表单的留言。",
    usersTitle: "用户",
    usersSubtitle: "账户、角色与访问权限。",
    auditTitle: "审计日志",
    auditSubtitle: "管理操作的记录。此处的条目无法编辑或删除。",

    filterStatus: "状态",
    filterEmail: "邮箱",
    filterEmailPlaceholder: "name@example.org",
    filterFrom: "起始日期",
    filterTo: "截止日期",
    filterRole: "角色",
    filterAction: "操作",
    filterTarget: "对象",
    filterAny: "全部",
    applyFilters: "应用筛选",
    noResults: "没有符合筛选条件的记录。",
    open: "打开",
    exportCsv: "导出 CSV",
    exportStarted: "导出文件已下载。",
    previous: "上一页",
    next: "下一页",
    page: "第 {page} 页",
    system: "系统",

    colApplicant: "申请人",
    colInterest: "意向",
    colStatus: "状态",
    colSubmitted: "提交时间",
    colVideos: "录制",
    colActions: "操作",
    colUser: "用户",
    colRole: "角色",
    colVerified: "已验证",
    colLastSeen: "最近活跃",
    colWhen: "时间",
    colActor: "管理员",
    colAction: "操作",
    colTarget: "对象",
    colDetails: "详情",

    applicationDetails: "申请详情",
    videosHeading: "录制",
    noVideos: "该申请没有关联的录制内容。",
    updateStatus: "更新状态",
    statusNote: "本次变更的备注（可选）",
    internalNotes: "内部备注",
    internalOnly: "仅管理员可见，申请人永远看不到这些备注。",
    noNotes: "暂无内部备注。",
    addNote: "添加备注",
    noteAdded: "备注已添加。",
    statusUpdated: "状态已更新。",
    deleteSubmission: "删除申请",
    deleteConfirmTitle: "确定删除这份申请吗？",
    deleteConfirmBody:
      "该申请、其内部备注以及关联的录制内容都会被删除，此操作无法撤销。",
    deleted: "申请已删除。",
    deletedWithPending:
      "申请已删除。有 {count} 个录制文件暂时未能移除，已加入重试队列。",

    roles: {
      user: "普通用户",
      admin: "管理员",
    },
    grantAdmin: "设为管理员",
    revokeAdmin: "取消管理员",
    disableUser: "停用账户",
    enableUser: "启用账户",
    disabled: "已停用",
    userUpdated: "账户已更新。",
    roleConfirmTitle: "确定变更该账户的角色吗？",
    roleConfirmBody:
      "管理员可以查看所有申请、所有录制内容以及所有内部备注。变更 {email} 的角色会使该账户当前的登录会话失效。",
    disableConfirmTitle: "确定变更该账户的访问权限吗？",
    disableConfirmBody:
      "被停用的账户无法登录。此操作不会删除 {email} 的任何数据。",

    antiAbuse: "防滥用校验",
    antiAbuseNotChecked: "未校验——当时未配置任何服务",
    antiAbusePassed: "通过",
    antiAbuseFailed: "未通过",

    bootstrapTitle: "创建第一个管理员",
    bootstrapIntro:
      "系统不设置任何默认管理员账号。此页面只使用一次，用于给已有账户授予管理员角色。",
    bootstrapRequirementsHeading: "以下条件同时满足时此页面才可用",
    bootstrapRequirement1: "该部署已在服务端密钥存储中配置初始化密钥。",
    bootstrapRequirement2:
      "系统中尚不存在任何管理员。一旦存在，此页面即永久失效。",
    bootstrapRequirement3: "下方填写的账户已完成注册，且邮箱已验证。",
    bootstrapSecret: "初始化密钥",
    bootstrapSecretHint:
      "该密钥保存在 Secret Manager 中，不会写入本页面、代码仓库或前端构建产物。",
    bootstrapButton: "授予管理员角色",
    bootstrapDoneHeading: "管理员已创建",
    bootstrapDoneBody:
      "已将管理员角色授予 {email}。请重新登录，使新的会话带上该角色。",

    notAdminTitle: "需要管理员权限",
    notAdminBody: "此区域受限访问。你的账户没有管理员权限。",
  },

  common: {
    loading: "加载中…",
    loadMore: "加载更多",
    retry: "重试",
    back: "返回",
    close: "关闭",
    cancel: "取消",
    save: "保存",
    saving: "正在保存…",
    delete: "删除",
    confirm: "确认",
    yes: "是",
    no: "否",
    none: "无",
    notProvided: "未填写",
    search: "搜索",
    copied: "已复制",
    learnMore: "了解更多",
    requiredFields: "标记为必填的字段必须填写。",
    unsavedChanges: "你有尚未保存的修改。",
    offline: "你似乎处于离线状态。请检查网络连接后重试。",
  },

  errors: {
    title: "出现了问题",
    generic: "出现了问题，请重试。",
    network: "无法连接到服务器。请检查网络连接后重试。",
    validation_failed: "请修正标注的字段。",
    unauthenticated: "请先登录后继续。",
    email_not_verified: "请先完成邮箱验证。",
    forbidden: "你没有执行该操作的权限。",
    not_found: "未找到该内容。",
    rate_limited: "尝试次数过多，请稍后再试。",
    payload_too_large: "文件过大。",
    unsupported_media_type: "不支持该文件格式。",
    submission_not_editable: "该申请已提交，无法继续编辑。",
    submission_not_submittable: "该申请当前状态无法提交。",
    submission_not_deletable: "只有草稿才能删除。",
    consent_to_contact_required: "请确认允许我们与你联系。",
    consent_to_privacy_required: "请确认你已阅读隐私说明。",
    video_quota_exceeded: "你保存的录制数量已达上限。",
    video_not_playable: "该录制内容当前无法播放。",
    video_already_uploaded: "该录制内容已经上传过了。",
    upload_object_missing: "未找到已上传的文件，请重新上传。",
    upload_empty: "录制内容为空，请重新录制。",
    recording_too_long: "录制时长超过了允许的上限。",
    bootstrap_already_used: "系统中已存在管理员。",
    last_admin: "必须至少保留一位管理员。",
    cannot_demote_self: "不能取消自己的管理员权限。",
    cannot_disable_self: "不能停用自己的账户。",
    adapter_not_configured: "该功能在当前部署地区尚未配置。",
    internal_error: "我们这边出现了问题，请稍后重试。",
    notFoundTitle: "页面未找到",
    notFoundBody: "你请求的页面不存在，或者已经移动。",
    notFoundAction: "返回首页",
    auth: {
      invalid_credentials: "邮箱与密码不匹配。",
      email_in_use: "该邮箱已经注册过账户。",
      weak_password: "请使用更长的密码——至少 12 个字符。",
      invalid_email: "请输入有效的邮箱地址。",
      too_many_requests: "尝试次数过多，请几分钟后再试。",
      user_disabled: "该账户已被停用。如果你认为这是误操作，请与我们联系。",
      requires_recent_login: "请重新登录以完成此项变更。",
      network: "无法连接登录服务。请检查网络连接后重试。",
      unknown: "登录失败，请重试。",
    },
  },

  legal: {
    privacyTitle: "隐私说明",
    privacyUpdated: "本说明描述本网站当前的实际做法。",
    privacyCollectHeading: "我们收集什么",
    privacyCollectBody:
      "如果你发送联系表单，我们会保存你填写的姓名、邮箱、意向、所属机构与留言内容。如果你创建账户，我们会保存你的邮箱、显示名称，以及你选填的机构与电话。如果你提交申请，我们会保存你的填写内容。如果你录制视频自我介绍，我们会保存视频文件及其大小、格式与状态。",
    privacyAssessmentHeading: "关于 GrowthWise 自查",
    privacyAssessmentBody:
      "自查的作答完全在你的浏览器中处理，不会传输给我们，也不会被保存到任何地方。",
    privacyUseHeading: "我们如何使用这些信息",
    privacyUseBody:
      "我们使用你提供的信息来回复你，以及管理你咨询的项目。我们不会出售这些信息，也不会将其用于广告。",
    privacySecurityHeading: "存储与访问",
    privacySecurityBody:
      "数据存储在美国。视频录制内容是私密的：没有公开链接，播放需要经过身份验证、单独授权且有效期很短的链接。GrowthWise Education 的管理员为了处理申请，可以查看申请内容并播放录制。",
    privacyRetentionHeading: "保存多久",
    privacyRetentionBody:
      "申请与录制内容的保留期限尚未确定。在确定之前，系统不会自动删除任何内容，你随时可以在个人中心删除自己的草稿与录制。",
    privacyRightsHeading: "你的选择",
    privacyRightsBody:
      "你可以在个人中心修改资料、删除草稿与删除录制内容。其他事项请通过联系页面上的邮箱与我们联系。",
    privacyContactHeading: "有疑问",
    privacyContactBody: "请联系 hello@growthwiseeducation.org。",
    termsTitle: "使用条款",
    termsEducationalHeading: "教育用途",
    termsEducationalBody:
      "GrowthWise Education 提供教育项目与教育材料。本网站上的任何内容都不构成医学或心理学诊断，也不能替代具备资质的医疗或心理健康专业人员的评估与照护。",
    termsAccountHeading: "你的账户",
    termsAccountBody:
      "请妥善保管密码；如果你认为他人使用了你的账户，请告知我们。你对自己提交的内容负责，包括你录制的内容。",
    termsContentHeading: "你上传的内容",
    termsContentBody:
      "请只上传你本人的录制内容，并在你愿意被录制的环境中录制。请勿上传任何违法内容，也不要在未经他人同意的情况下录入他人。",
    termsAvailabilityHeading: "服务可用性",
    termsAvailabilityBody:
      "这是一个持续运行的服务，可能会发生变化。为保护服务或其使用者，我们可能暂停访问。",
    termsCrisisHeading: "紧急情况",
    termsCrisisBody:
      "本网站不会对紧急情况进行实时监控。在美国境内如需紧急或危机支持，请拨打或发送短信至 988。在其他国家或地区，请联系当地相应的紧急或危机支持服务。",
  },

  notConfigured: {
    title: "尚未配置",
    body: "该功能在 {region} 部署中尚未配置任何服务商。它目前只是一个已定义的接口，等待具体实现，而不是已经接入的功能。",
    region: "部署地区：{region}",
  },
};
